import React from 'react';
import CHAMPIONSHIPS from '../../data/championships';
import { getMatchTitleLabel, getMatchTitleValues } from '../../utils/matchTitles';
import '../../styles/ShowEditor.css';

function MatchManager({ matches, onEditMatch, onDeleteMatch, editingMatchId }) {
  const getTeamDisplayName = (team) => {
    const participantNames = (team.participantsDetails || [])
      .map((wrestler) => wrestler.name)
      .filter(Boolean);

    return participantNames.length > 0 ? participantNames.join(' & ') : team.name;
  };

  const getMatchHeadline = (match) => {
    if (match.isMultiTeam) {
      return (match.teamsDetails || [])
        .map((team) => getTeamDisplayName(team))
        .filter(Boolean)
        .join(' vs ');
    }

    return (match.participantsDetails || [])
      .map((wrestler) => wrestler.name)
      .filter(Boolean)
      .join(' vs ');
  };

  const isWinningTeam = (match, team) =>
    Boolean(match.winner && match.winner !== 'no_contest' && `team${team.id}` === match.winner);

  const isWinningParticipant = (match, participant) =>
    Boolean(match.winner && match.winner !== 'no_contest' && participant.id === match.winner);

  const renderParticipantNames = (participants = [], isWinner = false, keyPrefix = 'participant') => {
    const names = participants.map((wrestler) => wrestler.name).filter(Boolean);

    if (names.length === 0) return null;

    return names.map((name, index) => (
      <React.Fragment key={`${keyPrefix}-${name}-${index}`}>
        {index > 0 && <span className="show-match-item__team-divider">&</span>}
        <strong className={`show-match-item__participant ${isWinner ? 'is-winner' : ''}`}>
          {name}
        </strong>
      </React.Fragment>
    ));
  };

  const renderMatchParticipants = (match) => {
    if (match.isMultiTeam) {
      return (match.teamsDetails || [])
        .map((team) => {
          const winner = isWinningTeam(match, team);
          const teamParticipants = renderParticipantNames(
            team.participantsDetails || [],
            winner,
            `team-${team.id}`
          );

          return (
            <span
              key={team.id}
              className={`show-match-item__side ${winner ? 'is-winner' : ''}`}
            >
              {teamParticipants || (
                <strong className="show-match-item__participant">
                  {team.name}
                </strong>
              )}
            </span>
          );
        })
        .filter(Boolean);
    }

    return (match.participantsDetails || [])
      .map((participant) => {
        const winner = isWinningParticipant(match, participant);

        return participant.name ? (
          <span
            key={participant.id}
            className={`show-match-item__side ${winner ? 'is-winner' : ''}`}
          >
            <strong className={`show-match-item__participant ${winner ? 'is-winner' : ''}`}>
              {participant.name}
            </strong>
          </span>
        ) : null;
      })
      .filter(Boolean);
  };

  return (
    <div className="card shadow-sm border-0 h-100 show-editor-card show-match-manager">
      <div className="card-header bg-success text-white py-3 show-editor-card__header show-editor-card__header--success">
        <div className="show-editor-toolbar">
          <div className="show-editor-toolbar__copy">
            <h3 className="h5 mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Match
            </h3>
          </div>
        </div>
      </div>
      <div className="card-body p-0 show-editor-card__body">
        <div className="list-group list-group-flush">
          {matches.length === 0 ? (
            <div className="text-center py-4 text-muted show-editor-empty">
              <i className="bi bi-inbox display-4 d-block mb-2"></i>
              Nessun match creato
            </div>
          ) : (
            [...matches]
              .sort((a, b) => {
                if (a.timestamp && b.timestamp) {
                  return a.timestamp - b.timestamp;
                }
                if (a.timestamp && !b.timestamp) return -1;
                if (!a.timestamp && b.timestamp) return 1;
                return 0;
              })
              .map((match) => {
                const headline = getMatchHeadline(match);
                const participantItems = renderMatchParticipants(match);
                const titleValues = getMatchTitleValues(match);

                return (
                  <div
                    key={match.id}
                    className={`list-group-item list-group-item-action show-match-item ${editingMatchId === match.id ? 'is-editing' : ''}`}
                  >
                    <div className="show-match-item__layout">
                      <div className="show-match-item__content">
                        <div className="show-match-item__participants">
                          {participantItems.length > 0 ? participantItems : (
                            <span className="show-match-item__empty">
                              {headline || 'Match senza partecipanti'}
                            </span>
                          )}
                        </div>
                        <div className="show-match-item__summary">
                          {match.stipulation ? <span>{match.stipulation}</span> : null}
                          {titleValues.length > 0 ? (
                            <span className="show-match-item__title-badges">
                              {titleValues.map((titleValue) => (
                                <span
                                  key={`${match.id}-${titleValue}`}
                                  className="show-match-item__title-badge"
                                >
                                  {getMatchTitleLabel({ title: titleValue }, CHAMPIONSHIPS)}
                                </span>
                              ))}
                            </span>
                          ) : null}
                          {match.winner === 'no_contest' ? <span>No-Contest</span> : null}
                          {match.isDisqualificationWin ? <span>DQ</span> : null}
                        </div>
                      </div>
                      <div className="btn-group btn-group-sm show-match-item__actions">
                        <button
                          className="btn btn-outline-success"
                          onClick={() => onEditMatch(match)}
                          title="Modifica"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => onDeleteMatch(match.id)}
                          title="Elimina"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      </div>
    </div>
  );
}

export default MatchManager;
